import { useState } from "react";
import { AlertTriangle, Ban, Check, Link2, Pencil, Quote, Split, Wrench, X } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { editRule, setRuleDecision } from "./api";
import { ATTENTION_COPY, VerityRule } from "./types";

interface RuleCardProps {
  rule: VerityRule;
  /** Every rule of the standard, so a relation can be shown by the label it points at. */
  allRules: VerityRule[];
  standardId: string;
  selected: boolean;
  checked: boolean;
  busy: boolean;
  onFocus: () => void;
  onToggleChecked: () => void;
  onSplit: () => void;
  onChanged: () => Promise<void>;
}

/**
 * What this requirement's status actually is.
 *
 * Machine and human are reported separately and in that order of precedence: once a person has acted,
 * their decision is the headline. Before that, the honest label is what the machine could establish —
 * "Source verified", never "Approved", because nobody approved it.
 */
function statusOf(rule: VerityRule): { status: "pass" | "warning" | "fail" | "info"; label: string } {
  if (rule.decision === "approved") return { status: "pass", label: "Human reviewed" };
  if (rule.decision === "edited") return { status: "pass", label: "Human reviewed · edited" };
  if (rule.decision === "excluded") return { status: "fail", label: "Excluded" };
  if (rule.decision === "needs-review") return { status: "fail", label: "Flagged for review" };
  if (rule.validation === "verified") return { status: "pass", label: "Source verified" };
  if (rule.validation === "review-recommended") return { status: "warning", label: "Review recommended" };
  return { status: "fail", label: "Action required" };
}

const RELATION_COPY: Record<string, string> = {
  "sibling-range": "paired with",
  "exception-path": "applies when not met:",
  constrains: "constrains",
};

function predicateOf(p: VerityRule["predicate"]): string | null {
  if (p.kind === "threshold") return `${p.operator} ${p.value} ${p.unit ?? ""}`.trim();
  if (p.kind === "compound") return `all of: ${(p.parts ?? []).join(", ")}`;
  if (p.kind === "lookup") return `lookup by ${p.lookup?.keyBy ?? "key"} — ${p.lookup?.rows?.length ?? 0} rows`;
  return null;
}

/**
 * One candidate requirement, with everything needed to judge it.
 *
 * The source half is not editable and never will be. A reviewer decides what a sentence MEANS; they
 * do not get to change what it SAYS — that boundary is what keeps the library tied to its documents,
 * and it is enforced in the API as well as here.
 */
const RuleCard = ({
  rule,
  allRules,
  standardId,
  selected,
  checked,
  busy,
  onFocus,
  onToggleChecked,
  onSplit,
  onChanged,
}: RuleCardProps) => {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(rule.subject);
  const [requirement, setRequirement] = useState(rule.requirement);
  const [saving, setSaving] = useState(false);

  const badge = statusOf(rule);
  const unresolved = rule.validation !== "verified" && !rule.decision;
  const blocking = rule.validation === "action-required" && !rule.decision;
  const predicate = predicateOf(rule.predicate);

  // Only shown when the proposal actually differs — a "correction" identical to what is already
  // there is noise, and reading it as a change would be worse than noise.
  const suggested = rule.repair?.suggestion;
  const suggestedSubject =
    suggested?.subject && suggested.subject !== rule.subject ? suggested.subject : undefined;
  const suggestedRequirement =
    suggested?.requirement && suggested.requirement !== rule.requirement
      ? suggested.requirement
      : undefined;

  /**
   * A predicate suggestion naming a `kind` replaces the predicate; one without a kind is a patch.
   *
   * "Give this threshold a unit of dimensionless" arrives as {unit: "dimensionless"} — applying that
   * as a whole predicate would silently throw away the operator and the value, which is a worse rule
   * than the one being repaired.
   */
  const mergedPredicate = suggested?.predicate
    ? suggested.predicate.kind
      ? suggested.predicate
      : { ...rule.predicate, ...suggested.predicate }
    : undefined;

  const act = async (work: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await work();
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const save = () =>
    act(async () => {
      await editRule(standardId, rule.ruleId, { subject, requirement });
      setEditing(false);
    });

  /**
   * Applies Verity's proposal, as a human decision.
   *
   * Recorded as `edited` rather than `approved` because that is what happened: the rule now says
   * something different from what was extracted, and a published set has to be able to show that.
   */
  const applyRepair = () =>
    act(async () => {
      if (!suggested) return;
      if (suggested.exclude) {
        await setRuleDecision(standardId, rule.ruleId, "excluded");
        return;
      }
      await editRule(standardId, rule.ruleId, {
        ...(suggested.subject ? { subject: suggested.subject } : {}),
        ...(suggested.requirement ? { requirement: suggested.requirement } : {}),
        ...(suggested.ruleType ? { ruleType: suggested.ruleType } : {}),
        ...(suggested.predicate ? { predicate: mergedPredicate! } : {}),
        ...(suggested.externalRefs ? { externalRefs: suggested.externalRefs } : {}),
      });
    });

  /** Opens the ordinary editor pre-filled with the proposal, so a near-miss is a tweak, not a retype. */
  const startEditFromRepair = () => {
    setSubject(suggested?.subject ?? rule.subject);
    setRequirement(suggested?.requirement ?? rule.requirement);
    setEditing(true);
  };

  return (
    <div
      className={`vr-rule ${selected ? "vr-selected" : ""} ${checked ? "vr-checked" : ""} vr-rule-${badge.status}`}
      onClick={onFocus}
    >
      <div className="vr-rule-head">
        <input
          type="checkbox"
          className="vr-rule-check"
          checked={checked}
          onChange={onToggleChecked}
          onClick={(e) => e.stopPropagation()}
          title="Select for merge"
        />
        <span className="vr-rule-id">{rule.displayId}</span>
        <span className="vr-rule-type">{rule.ruleType}</span>
        <StatusBadge status={badge.status} label={badge.label} />
      </div>

      {/* Why the machine sent this here. A reviewer who is told what to look at settles it in
          seconds; one who is only told "review this" has to re-derive the concern first. */}
      {unresolved && rule.attention.length > 0 && (
        <ul className={`vr-attention-list ${blocking ? "vr-blocking" : ""}`}>
          {rule.attention.map((reason) => (
            <li key={reason}>
              <AlertTriangle size={11} /> {ATTENTION_COPY[reason] ?? reason}
            </li>
          ))}
        </ul>
      )}

      {/* And what Verity proposes doing about it.
          "Incomplete predicate" is a diagnosis, not an answer — it names a defect and leaves the work
          with the reviewer. Verity has the sentence, the extracted rule and the reason it failed, so
          it states what is wrong with THIS rule and proposes the correction. One click to accept. */}
      {unresolved && rule.repair && !rule.repair.failure && (
        <div className="vr-repair" onClick={(e) => e.stopPropagation()}>
          <div className="vr-repair-head">
            <Wrench size={11} /> Verity&rsquo;s reading of the problem
            <span className="vr-cell-dim">
              {Math.round(rule.repair.confidence * 100)}% confident
            </span>
          </div>
          {rule.repair.diagnosis && <p className="vr-repair-diagnosis">{rule.repair.diagnosis}</p>}

          {rule.repair.suggestion ? (
            <>
              <div className="vr-repair-label">Suggested correction</div>
              {rule.repair.suggestion.exclude ? (
                <p className="vr-repair-text">
                  <strong>Exclude this requirement</strong> — the sentence supports no independently
                  actionable obligation.
                </p>
              ) : (
                <div className="vr-repair-diff">
                  {suggestedSubject !== undefined && (
                    <div>
                      <span className="vr-cell-dim">Subject</span>
                      <span className="vr-repair-was">{rule.subject}</span>
                      <span className="vr-repair-now">{suggestedSubject}</span>
                    </div>
                  )}
                  {suggestedRequirement !== undefined && (
                    <div>
                      <span className="vr-cell-dim">Requirement</span>
                      <span className="vr-repair-was">{rule.requirement}</span>
                      <span className="vr-repair-now">{suggestedRequirement}</span>
                    </div>
                  )}
                  {rule.repair.suggestion.ruleType && (
                    <div>
                      <span className="vr-cell-dim">Type</span>
                      <span className="vr-repair-was">{rule.ruleType}</span>
                      <span className="vr-repair-now">{rule.repair.suggestion.ruleType}</span>
                    </div>
                  )}
                  {rule.repair.suggestion.predicate && (
                    <div>
                      <span className="vr-cell-dim">Predicate</span>
                      <span className="vr-repair-was">{predicate ?? rule.predicate.kind}</span>
                      <span className="vr-repair-now">
                        {(mergedPredicate && predicateOf(mergedPredicate)) ??
                          mergedPredicate?.kind ??
                          rule.repair.suggestion.predicate.kind}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {rule.repair.rationale && (
                <p className="vr-repair-rationale">{rule.repair.rationale}</p>
              )}
              <div className="vr-rule-actions">
                <button
                  className="vr-rowbtn"
                  disabled={busy || saving}
                  onClick={() => void applyRepair()}
                >
                  <Check size={12} />{" "}
                  {rule.repair.suggestion.exclude ? "Accept — exclude it" : "Accept correction"}
                </button>
                <button className="vr-rowbtn" disabled={busy || saving} onClick={startEditFromRepair}>
                  <Pencil size={12} /> Edit the correction
                </button>
                <button
                  className="vr-rowbtn"
                  disabled={busy || saving}
                  onClick={() => void act(() => setRuleDecision(standardId, rule.ruleId, "approved"))}
                  title="The rule is right as extracted; the flag is a false positive"
                >
                  <X size={12} /> Reject — it is already correct
                </button>
              </div>
            </>
          ) : (
            <p className="vr-repair-rationale">
              No correction could be proposed from the source sentence, so this one does need you.
            </p>
          )}
        </div>
      )}

      {editing ? (
        <div className="vr-rule-edit" onClick={(e) => e.stopPropagation()}>
          <div className="vr-field">
            <label>Subject</label>
            <input className="vr-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="vr-field">
            <label>Requirement</label>
            <input
              className="vr-input"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
            />
          </div>
          <div className="vr-rule-actions">
            <button className="vr-rowbtn" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button className="vr-rowbtn" onClick={() => void save()} disabled={saving}>
              <Check size={12} /> Save
            </button>
          </div>
        </div>
      ) : (
        <p className="vr-rule-text">
          <strong>{rule.subject}</strong> — {rule.requirement}
        </p>
      )}

      {predicate && <div className="vr-rule-predicate">{predicate}</div>}

      {rule.predicate.kind === "lookup" && (
        <div className="vr-rule-rows">
          {(rule.predicate.lookup?.rows ?? []).map((row, i) => (
            <div key={i}>
              <span>{row.key}</span>
              <span className="vr-cell-dim">{row.ref ?? String(row.value)}</span>
            </div>
          ))}
        </div>
      )}

      {(rule.applicability.own.length > 0 || rule.applicability.inherited.length > 0) && (
        <div className="vr-rule-meta">
          Applies to: {[...rule.applicability.own, ...rule.applicability.inherited].join("; ")}
        </div>
      )}
      {rule.conditions.length > 0 && (
        <div className="vr-rule-meta">Conditions: {rule.conditions.join("; ")}</div>
      )}
      {rule.exceptions.length > 0 && (
        <div className="vr-rule-meta">Exceptions: {rule.exceptions.join("; ")}</div>
      )}
      {rule.externalRefs.length > 0 && (
        <div className="vr-rule-meta">Refers to: {rule.externalRefs.join("; ")}</div>
      )}

      {rule.relations.map((rel, i) => {
        const target = allRules.find((r) => r.ruleId === rel.targetRuleId);
        return (
          <div key={i} className="vr-rule-relation">
            <Link2 size={11} /> {RELATION_COPY[rel.type] ?? rel.type}{" "}
            <strong>{target ? `${target.subject} — ${target.requirement}` : rel.targetRuleId}</strong>
          </div>
        );
      })}

      {/* Traceability. Not editable, and shown on every card rather than behind a disclosure — a rule
          whose source you have to go looking for is a rule people stop checking. */}
      <blockquote className="vr-rule-quote">
        <Quote size={10} />
        <span>“{rule.sourceQuote}”</span>
        <span className="vr-quote-src">
          §{rule.sectionNumber} · printed page {rule.printedPage ?? "—"} · pdf {rule.pdfPage}
        </span>
      </blockquote>

      {rule.derivation && rule.derivation !== "extracted" && (
        <div className="vr-rule-meta vr-rule-derived">
          {rule.derivation === "split" && "Split from an earlier candidate"}
          {rule.derivation === "merge" && "Merged from earlier candidates"}
          {rule.derivation === "manual" && "Added by hand"}
          {rule.derivation === "recovered" && "Recovered by the second pass over a possible gap"}
          {rule.reviewedAt && <> · {new Date(rule.reviewedAt).toLocaleDateString()}</>}
        </div>
      )}

      <div className="vr-rule-actions" onClick={(e) => e.stopPropagation()}>
        <button
          className="vr-rowbtn"
          disabled={busy || saving}
          onClick={() => void act(() => setRuleDecision(standardId, rule.ruleId, "approved"))}
          title={
            unresolved
              ? "Settle this — the requirement is correctly represented"
              : "Confirm you have read this. It is already source verified."
          }
        >
          <Check size={12} /> {unresolved ? "Approve" : "Confirm"}
        </button>
        <button className="vr-rowbtn" disabled={busy || saving} onClick={() => setEditing(true)}>
          <Pencil size={12} /> Edit
        </button>
        <button
          className="vr-rowbtn"
          disabled={busy || saving}
          onClick={onSplit}
          title="This is more than one requirement"
        >
          <Split size={12} /> Split
        </button>
        <button
          className="vr-rowbtn"
          disabled={busy || saving}
          onClick={() => void act(() => setRuleDecision(standardId, rule.ruleId, "needs-review"))}
          title="Flag for someone else to settle"
        >
          <AlertTriangle size={12} /> Needs review
        </button>
        <button
          className="vr-rowbtn vr-danger"
          disabled={busy || saving}
          onClick={() => void act(() => setRuleDecision(standardId, rule.ruleId, "excluded"))}
          title="Not a requirement — exclude it"
        >
          <Ban size={12} /> Exclude
        </button>
      </div>
    </div>
  );
};

export default RuleCard;
